import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/admin/AuthContext";
import {
  BookOpen,
  Crown,
  ShieldAlert,
  AlertTriangle,
  Search,
  LogOut,
  Settings,
  Lock,
  ChevronRight,
  Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_MAP = {
  BookOpen,
  Crown,
  ShieldAlert,
  AlertTriangle,
};

const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

const highlight = (text, q) => {
  if (!q || q.length < 2) return text;
  try {
    const re = new RegExp(`(${escapeRegex(q)})`, "ig");
    const parts = String(text).split(re);
    return parts.map((p, i) =>
      re.test(p) ? (
        <mark key={i} className="staff-mark">
          {p}
        </mark>
      ) : (
        <span key={i}>{p}</span>
      ),
    );
  } catch {
    return text;
  }
};

export default function StaffBible() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bible, setBible] = useState(null);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(0);
  const [query, setQuery] = useState("");
  const contentRef = useRef(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/staff/bible`, {
          withCredentials: true,
        });
        if (alive) setBible(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Impossibile caricare il documento");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const chapters = useMemo(
    () => (bible && Array.isArray(bible.chapters) ? bible.chapters : []),
    [bible],
  );

  const meta = bible?.meta || {};

  // Build search hits across all chapters
  const hits = useMemo(() => {
    if (!query || query.length < 2) return null;
    const q = query.toLowerCase();
    return chapters
      .map((c, ci) => {
        const matches = (c.sections || []).flatMap((s, si) => {
          const inH = (s.h || "").toLowerCase().includes(q);
          const inBody = (s.body || "").toLowerCase().includes(q);
          return inH || inBody ? [{ ci, si, sec: s }] : [];
        });
        const inTitle = (c.title || "").toLowerCase().includes(q);
        return matches.length > 0 || inTitle ? { c, ci, matches, inTitle } : null;
      })
      .filter(Boolean);
  }, [chapters, query]);

  const onLogout = async () => {
    await logout();
    navigate("/staff/login", { replace: true });
  };

  if (err) {
    return (
      <div className="staff-shell min-h-screen flex items-center justify-center px-4">
        <div className="staff-card rounded-2xl p-8 max-w-md text-center">
          <ShieldAlert size={32} className="text-[#E26B6B] mx-auto mb-4" />
          <h2 className="font-display text-xl uppercase tracking-wide text-[#F5F5F5] mb-2">
            Accesso negato
          </h2>
          <p className="text-sm text-[#B6B6BE]">{err}</p>
          <button
            onClick={() => navigate("/staff/login")}
            className="mt-6 staff-submit rounded-lg px-5 py-3 text-xs uppercase tracking-[0.28em] font-bold"
          >
            Torna al terminale
          </button>
        </div>
      </div>
    );
  }

  if (!bible) {
    return (
      <div className="staff-shell min-h-screen flex items-center justify-center">
        <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#B6B6BE]">
          Decifrazione in corso…
        </p>
      </div>
    );
  }

  const activeChapter = chapters[active];
  const ActiveIcon = (activeChapter && ICON_MAP[activeChapter.icon]) || BookOpen;

  return (
    <div className="staff-shell min-h-screen" data-testid="staff-bible-root">
      <div className="staff-scanlines" aria-hidden="true" />
      <div className="staff-vignette" aria-hidden="true" />
      <div className="staff-watermark-bg" aria-hidden="true">
        <span>INTERNO · NON DIVULGARE · INTERNO · NON DIVULGARE · </span>
        <span>INTERNO · NON DIVULGARE · INTERNO · NON DIVULGARE · </span>
      </div>

      {/* Topbar */}
      <header className="staff-topbar relative z-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Lock size={14} className="text-[#E26B6B] shrink-0" />
            <span className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#E26B6B]/85 hidden sm:inline">
              Terminale Sicuro
            </span>
            <span className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#B6B6BE]/40">·</span>
            <span className="font-mono-pl text-[10px] uppercase tracking-[0.22em] text-[#F5F5F5] truncate">
              {user?.email}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/admin/bible"
              data-testid="staff-edit-bible"
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] hover:text-[#F5F5F5] hover:bg-white/[0.04]"
              title="Modifica nel pannello admin"
            >
              <Settings size={12} /> Modifica
            </a>
            <button
              onClick={onLogout}
              data-testid="staff-logout"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[10px] uppercase tracking-[0.28em] text-[#B6B6BE] hover:text-[#E26B6B] hover:bg-[#E26B6B]/10"
            >
              <LogOut size={12} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 py-10 sm:py-16 grid lg:grid-cols-[280px,1fr] gap-10">
        {/* Sidebar — Chapter nav + search */}
        <aside className="staff-sidebar">
          <div className="mb-8">
            <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#E26B6B] mb-3">
              {meta.edition || "Vol. I"}
            </p>
            <h1 className="font-display text-2xl uppercase text-[#F5F5F5] tracking-wide leading-tight">
              {meta.title || "La Bibbia della Regia"}
            </h1>
            <p className="mt-2 font-mono-pl text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE]/70 italic">
              {meta.subtitle}
            </p>
          </div>

          <div className="staff-search relative mb-6">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#B6B6BE]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="staff-bible-search"
              placeholder="Cerca nel documento…"
              className="staff-input w-full pl-9 pr-3 py-2.5 rounded-lg text-xs text-[#F5F5F5] font-mono-pl tracking-wide"
            />
          </div>

          {hits && (
            <div className="mb-6 staff-search-results" data-testid="staff-bible-search-results">
              <p className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#E26B6B]/85 mb-2">
                {hits.length === 0 ? "Nessuna corrispondenza" : `${hits.reduce((a, h) => a + Math.max(1, h.matches.length), 0)} risultati`}
              </p>
            </div>
          )}

          <nav>
            <p className="font-mono-pl text-[10px] uppercase tracking-[0.32em] text-[#B6B6BE]/70 mb-3">
              Capitoli
            </p>
            {chapters.map((c, i) => {
              const Icon = ICON_MAP[c.icon] || BookOpen;
              const isActive = i === active;
              const dim = hits && !hits.find((h) => h.ci === i);
              return (
                <button
                  key={c.key || i}
                  onClick={() => {
                    setActive(i);
                    contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  data-testid={`staff-bible-chapter-${i}`}
                  className={`staff-nav-item w-full text-left flex items-center gap-3 px-3 py-3 rounded-lg mb-1 transition-all ${
                    isActive ? "staff-nav-item--active" : ""
                  } ${dim ? "opacity-40" : ""}`}
                >
                  <Icon size={13} className="shrink-0" strokeWidth={1.8} />
                  <div className="flex-1 min-w-0">
                    <p className="font-mono-pl text-[9px] uppercase tracking-[0.22em] text-[#B6B6BE]/60">
                      Cap. {String(i + 1).padStart(2, "0")}
                    </p>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#F5F5F5] truncate">
                      {c.title}
                    </p>
                  </div>
                  {isActive && <ChevronRight size={12} className="text-[#E26B6B] shrink-0" />}
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main content */}
        <main ref={contentRef} className="staff-content min-h-[60vh]">
          {activeChapter ? (
            <article>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-xl bg-[#E26B6B]/10 border border-[#E26B6B]/35 flex items-center justify-center">
                  <ActiveIcon size={16} className="text-[#E26B6B]" strokeWidth={1.8} />
                </span>
                <div>
                  <p className="font-mono-pl text-[9px] uppercase tracking-[0.32em] text-[#E26B6B]">
                    Capitolo {String(active + 1).padStart(2, "0")}
                  </p>
                  <p className="font-mono-pl text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE]/70 italic">
                    {activeChapter.subtitle}
                  </p>
                </div>
              </div>

              <h2 className="font-display text-3xl sm:text-5xl uppercase text-[#F5F5F5] leading-[1] tracking-wide glow-gold-strong mb-10">
                {highlight(activeChapter.title || "", query)}
              </h2>

              {(activeChapter.sections || []).map((s, i) => {
                const matchesQ =
                  query.length >= 2 &&
                  (((s.h || "").toLowerCase().includes(query.toLowerCase())) ||
                    ((s.body || "").toLowerCase().includes(query.toLowerCase())));
                return (
                  <section
                    key={i}
                    data-testid={`staff-bible-section-${active}-${i}`}
                    className={`staff-section mb-8 ${matchesQ ? "staff-section--hit" : ""}`}
                  >
                    <h3 className="font-display text-xl sm:text-2xl uppercase text-[#F5F5F5] tracking-wide mb-3">
                      {highlight(s.h || "", query)}
                    </h3>
                    <div className="text-[#B6B6BE] leading-relaxed text-sm sm:text-base space-y-3">
                      {String(s.body || "")
                        .split(/\n\n+/)
                        .map((para, k) => (
                          <p key={k}>{highlight(para, query)}</p>
                        ))}
                    </div>
                  </section>
                );
              })}

              <footer className="mt-16 pt-8 border-t border-white/[0.06]">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-[#E26B6B]/75">
                    <Eye size={11} /> Documento sigillato · accesso loggato
                  </div>
                  <div className="flex gap-2">
                    {active > 0 && (
                      <button
                        onClick={() => setActive(active - 1)}
                        className="px-4 py-2 rounded-full border border-white/10 text-[10px] uppercase tracking-[0.22em] text-[#B6B6BE] hover:text-[#F5F5F5] hover:border-white/30"
                      >
                        ← Capitolo precedente
                      </button>
                    )}
                    {active < chapters.length - 1 && (
                      <button
                        onClick={() => setActive(active + 1)}
                        data-testid="staff-bible-next"
                        className="px-4 py-2 rounded-full border border-[#E26B6B]/40 text-[10px] uppercase tracking-[0.22em] text-[#E26B6B] hover:bg-[#E26B6B]/10"
                      >
                        Capitolo successivo →
                      </button>
                    )}
                  </div>
                </div>
              </footer>
            </article>
          ) : (
            <p className="text-sm text-[#B6B6BE] italic">Nessun capitolo nel documento.</p>
          )}
        </main>
      </div>
    </div>
  );
}
